// Bespoke, module-level, reference-counted session store for FX & Materials'
// in-canvas texture painting (docs/3d-content-pipeline/roadmap.md 3.2 Phase
// 3 / section 6). Shaped like panel-system/sharedModeDocument.ts's own
// acquire/release/refcount pattern, but for a different kind of shared
// state: a live `<canvas>`/`CanvasTexture` and raster undo/redo history, not
// a persisted JSON document. Needed because the brush controls (Undo/Redo/
// Save, in FxMaterialPropertiesPanel) and the paint canvas and pointer
// events (in FxMaterialPreviewPanel) are two independently-mounted sibling
// panels, not a parent/child pair - createTransientSelectionStore (this
// file's neighbour, used for the selected-material name) re-renders every
// listener on every set, which is fine for a rarely-changing selection but
// wrong for a value that can change dozens of times a second mid-stroke.
//
// Every action below that mutates the canvas notifies listeners at most
// once per stroke (on stroke start), never per stamp - the live visual
// update during a stroke goes through `texture.needsUpdate = true` alone,
// which R3F's default continuous frameloop picks up on its own next frame
// with no React re-render required (confirmed: no `frameloop="demand"`
// anywhere in this repo's Viewport/ViewportHost).
//
// A session is keyed by (projectId, materialId): switching the selected
// material tears down the old session (this file's `release`, on the
// consuming panels' effect cleanup) and starts a fresh one for the new
// material - in-progress, unsaved strokes on the material being switched
// away from are lost. `FxMaterialPropertiesPanel` gates that switch (and
// leaving Paint mode) behind a confirmation while `dirty` is true.
import { useEffect, useReducer } from 'react';
import { CanvasTexture, RepeatWrapping, SRGBColorSpace } from 'three';

import { dilateTexture, fillSolidColour, hexToRgb, interpolateStrokePoints, stampBrush } from '../../engine';

/** Fixed working resolution for every paint session - plenty of detail for
 * a hand-painted material texture, and small enough that a 1 MiB-per-
 * snapshot undo stack (see UNDO_LIMIT below) stays a reasonable amount of
 * memory for one open session. */
export const PAINT_CANVAS_SIZE = 512;

/** 512x512x4 = 1 MiB per snapshot - an order of magnitude bigger than any
 * other undo stack in this codebase (gunsmithStore's 64-entry cap holds
 * tiny JSON documents, not raw pixel buffers). Keep this cap low and
 * explicit; do not assume gunsmith's 64 is a safe number to copy here. */
const UNDO_LIMIT = 16;

/** How far apart (in canvas pixels) interpolated stamps land along a fast
 * stroke, as a fraction of the brush's own radius - dense enough that
 * individual stamps visibly overlap into one continuous line. */
const STAMP_SPACING_FRACTION = 0.35;

export type PaintMode = 'view' | 'paint';

export interface BrushSettings {
  sizePx: number;
  hardness: number;
  opacity: number;
  colourHex: string;
}

const DEFAULT_BRUSH: BrushSettings = { sizePx: 28, hardness: 0.6, opacity: 1, colourHex: '#ffffff' };

interface Entry {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  texture: CanvasTexture;
  mode: PaintMode;
  brush: BrushSettings;
  dirty: boolean;
  /** Set true the first time `seedFrom`/`seedSolid` runs for this entry, so
   * a consumer entering Paint mode again later (having already seeded and
   * possibly painted) does not seed a second time over real work. Resets to
   * false naturally on the next fresh `createEntry` once refcount hits zero
   * and the entry is torn down. */
  seeded: boolean;
  undoStack: Uint8ClampedArray[];
  redoStack: Uint8ClampedArray[];
  lastPoint: { u: number; v: number } | null;
  refCount: number;
  listeners: Set<() => void>;
}

const entries = new Map<string, Entry>();

function keyFor(projectId: string, materialId: string): string {
  return projectId + ':' + materialId;
}

function notify(entry: Entry): void {
  entry.listeners.forEach((l) => l());
}

function createEntry(): Entry {
  const canvas = document.createElement('canvas');
  canvas.width = PAINT_CANVAS_SIZE;
  canvas.height = PAINT_CANVAS_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not create a paint canvas.');
  const texture = new CanvasTexture(canvas);
  // Straight-alpha canvas 2D content, premultiplied by three.js's default,
  // would render a soft brush's partial-alpha edges as solid black - the
  // same bug spriteCanvas.ts's canvasTexture() documents and works around.
  texture.premultiplyAlpha = false;
  texture.colorSpace = SRGBColorSpace;
  // The sphere's U axis is a real 360-degree wrap, not a clamped edge -
  // RepeatWrapping lets the GPU sample across the seam as continuous;
  // stampBrush's own wrapping (texturePaint.ts) is what keeps both sides of
  // the seam painted consistently for that sampling to look right.
  texture.wrapS = RepeatWrapping;
  return {
    canvas, ctx, texture,
    mode: 'view',
    brush: { ...DEFAULT_BRUSH },
    dirty: false,
    seeded: false,
    undoStack: [],
    redoStack: [],
    lastPoint: null,
    refCount: 0,
    listeners: new Set(),
  };
}

function ensureEntry(projectId: string, materialId: string): Entry {
  const key = keyFor(projectId, materialId);
  const existing = entries.get(key);
  if (existing) return existing;
  const entry = createEntry();
  entries.set(key, entry);
  return entry;
}

function acquire(projectId: string, materialId: string): Entry {
  const entry = ensureEntry(projectId, materialId);
  entry.refCount += 1;
  return entry;
}

/** Releases this consumer's hold on the session. The last release disposes
 * the GPU texture and tears the entry down - UNLESS the session is still
 * `dirty` (unsaved strokes), in which case it deliberately survives at
 * refcount zero so a later `acquire` for the same key picks the same canvas
 * and undo history back up intact, rather than starting blank.
 *
 * This matters for a reason narrower than "switching materials" (that path
 * is already gated behind a confirmation, see this file's header comment):
 * `SystemsFxPanel`'s own Materials/Particles segmented control
 * (`src/studio/editor/systemsPanels.tsx`) is a plain React ternary, not a
 * dockview tab - flipping it unmounts `FxMaterialListPanel`,
 * `FxMaterialPreviewPanel` and `FxMaterialPropertiesPanel` all at once,
 * dropping this session's refcount to zero with no confirmation at all,
 * since that toggle has no reason to know this feature's dirty state.
 * Without this guard, checking Particles mid-paint silently discarded
 * every unsaved stroke. The accepted cost: a session left dirty and never
 * revisited (the creator paints, then genuinely navigates away for good
 * without saving) leaks one canvas/texture until the page reloads - a
 * small, bounded cost against silently destroying real work. */
function release(projectId: string, materialId: string): void {
  const key = keyFor(projectId, materialId);
  const entry = entries.get(key);
  if (!entry) return;
  entry.refCount -= 1;
  if (entry.refCount <= 0 && !entry.dirty) {
    entry.texture.dispose();
    entries.delete(key);
  }
}

function getEntry(projectId: string, materialId: string): Entry | undefined {
  return entries.get(keyFor(projectId, materialId));
}

/**
 * Forcibly disposes and removes a session for `(projectId, materialId)`
 * regardless of its dirty state or current refcount - the one case where
 * keeping a dirty session alive past `release` (above) is wrong rather than
 * a safety net: the material itself has just been deleted from the FxDoc,
 * so there is nothing left to ever resume painting for. Without this, a
 * material deleted while it had unsaved strokes would leak its canvas and
 * `CanvasTexture` for the rest of the page's lifetime, since nothing would
 * ever call `markSaved` on an id that no longer exists. Safe to call for a
 * key with no session at all. Call this from wherever a material is
 * actually removed from its document (`FxMaterialListPanel`'s delete flow),
 * not from any Paint-mode UI action.
 */
export function discardFxMaterialPaintSession(projectId: string, materialId: string): void {
  const key = keyFor(projectId, materialId);
  const entry = entries.get(key);
  if (!entry) return;
  entry.texture.dispose();
  entries.delete(key);
}

function readPixels(entry: Entry): Uint8ClampedArray {
  return entry.ctx.getImageData(0, 0, PAINT_CANVAS_SIZE, PAINT_CANVAS_SIZE).data;
}

function writePixels(entry: Entry, pixels: Uint8ClampedArray): void {
  entry.ctx.putImageData(new ImageData(new Uint8ClampedArray(pixels), PAINT_CANVAS_SIZE, PAINT_CANVAS_SIZE), 0, 0);
  entry.texture.needsUpdate = true;
}

function pushUndo(entry: Entry): void {
  entry.undoStack.push(readPixels(entry));
  if (entry.undoStack.length > UNDO_LIMIT) entry.undoStack.shift();
  entry.redoStack = [];
}

function resetHistory(entry: Entry): void {
  entry.undoStack = [];
  entry.redoStack = [];
  entry.dirty = false;
}

function setMode(entry: Entry, mode: PaintMode): void {
  entry.mode = mode;
  entry.lastPoint = null;
  notify(entry);
}

function setBrush(entry: Entry, changes: Partial<BrushSettings>): void {
  entry.brush = { ...entry.brush, ...changes };
  notify(entry);
}

/** Seeds the canvas from an already-decoded image (an existing albedo map,
 * so entering Paint mode on a textured material lets a creator touch it up
 * rather than always starting blank), scaling it to the fixed working
 * resolution. Resets undo/redo and `dirty` - this is a fresh starting
 * point, not something to undo back past.
 *
 * `drawImage` stretches non-uniformly if `width`/`height` are not already
 * square - a non-square existing albedo (nothing in the upload flow
 * enforces one) is silently distorted to fill the square paint canvas, and
 * saving over it makes that distortion permanent. Not corrected for v1:
 * this feature's working canvas is always square, and every texture this
 * app itself produces (the 256x256 procedural generators, this feature's
 * own 512x512 saves) already is, so the case is a deliberately accepted,
 * narrow gap - an unusually-shaped manually-uploaded albedo, not the
 * common path - rather than a silent letterbox/pad step worth the added
 * complexity here. */
function seedFrom(entry: Entry, pixels: Uint8ClampedArray, width: number, height: number): void {
  const source = document.createElement('canvas');
  source.width = width;
  source.height = height;
  const sctx = source.getContext('2d');
  if (!sctx) return;
  sctx.putImageData(new ImageData(new Uint8ClampedArray(pixels), width, height), 0, 0);
  entry.ctx.clearRect(0, 0, PAINT_CANVAS_SIZE, PAINT_CANVAS_SIZE);
  entry.ctx.drawImage(source, 0, 0, width, height, 0, 0, PAINT_CANVAS_SIZE, PAINT_CANVAS_SIZE);
  entry.texture.needsUpdate = true;
  resetHistory(entry);
  entry.seeded = true;
  notify(entry);
}

/** Seeds the canvas as one flat opaque colour - used when the material has
 * no existing albedo map yet. A `meshStandardMaterial` with no
 * `transparent`/`alphaTest` set multiplies `color` by the texture's RGB
 * only regardless of its alpha, so an unseeded, all-transparent canvas
 * would render the whole preview sphere solid black the instant Paint mode
 * is entered, before a single stroke - seeding opaque makes entering, then
 * leaving without painting, a visual no-op. */
function seedSolid(entry: Entry, colourHex: string): void {
  const pixels = new Uint8ClampedArray(PAINT_CANVAS_SIZE * PAINT_CANVAS_SIZE * 4);
  fillSolidColour(pixels, PAINT_CANVAS_SIZE, PAINT_CANVAS_SIZE, colourHex);
  writePixels(entry, pixels);
  resetHistory(entry);
  entry.seeded = true;
  notify(entry);
}

function stampAt(entry: Entry, points: Array<{ u: number; v: number }>): void {
  const imageData = entry.ctx.getImageData(0, 0, PAINT_CANVAS_SIZE, PAINT_CANVAS_SIZE);
  const colourRgb = hexToRgb(entry.brush.colourHex);
  for (const p of points) {
    stampBrush(imageData.data, PAINT_CANVAS_SIZE, PAINT_CANVAS_SIZE, p.u, p.v, entry.brush.sizePx, entry.brush.hardness, colourRgb, entry.brush.opacity);
  }
  entry.ctx.putImageData(imageData, 0, 0);
  entry.texture.needsUpdate = true;
}

/** Starts a new stroke at UV (`u`, `v`): pushes one undo snapshot (a stroke
 * is the undo granularity, not a stamp) and paints the first stamp. */
function beginStroke(entry: Entry, u: number, v: number): void {
  pushUndo(entry);
  entry.lastPoint = { u, v };
  stampAt(entry, [{ u, v }]);
  entry.dirty = true;
  notify(entry);
}

/** Continues the current stroke to UV (`u`, `v`), filling the gap from the
 * last sampled point with `interpolateStrokePoints` so a fast pointer move
 * does not leave visible gaps between stamps. Deliberately does not call
 * `notify()` - dozens of these can happen per second during one stroke, and
 * nothing any listener reads (`dirty`, `canUndo`, brush settings) changes
 * between them; the live visual update is `texture.needsUpdate` alone. */
function strokeTo(entry: Entry, u: number, v: number): void {
  const from = entry.lastPoint ?? { u, v };
  const spacingPx = Math.max(2, entry.brush.sizePx * STAMP_SPACING_FRACTION);
  const points = interpolateStrokePoints(from.u, from.v, u, v, spacingPx, PAINT_CANVAS_SIZE);
  stampAt(entry, points);
  entry.lastPoint = { u, v };
}

function endStroke(entry: Entry): void {
  entry.lastPoint = null;
}

function undo(entry: Entry): void {
  const previous = entry.undoStack.pop();
  if (!previous) return;
  entry.redoStack.push(readPixels(entry));
  if (entry.redoStack.length > UNDO_LIMIT) entry.redoStack.shift();
  writePixels(entry, previous);
  entry.dirty = true;
  notify(entry);
}

function redo(entry: Entry): void {
  const next = entry.redoStack.pop();
  if (!next) return;
  entry.undoStack.push(readPixels(entry));
  if (entry.undoStack.length > UNDO_LIMIT) entry.undoStack.shift();
  writePixels(entry, next);
  entry.dirty = true;
  notify(entry);
}

function clearTo(entry: Entry, colourHex: string): void {
  pushUndo(entry);
  const pixels = new Uint8ClampedArray(PAINT_CANVAS_SIZE * PAINT_CANVAS_SIZE * 4);
  fillSolidColour(pixels, PAINT_CANVAS_SIZE, PAINT_CANVAS_SIZE, colourHex);
  writePixels(entry, pixels);
  entry.dirty = true;
  notify(entry);
}

function markSaved(entry: Entry): void {
  entry.dirty = false;
  notify(entry);
}

/** Returns a dilated copy of the current canvas pixels for export, without
 * touching the live editable canvas or its undo history - dilation is a
 * save-time safety net (see texturePaint.ts's `dilateTexture` doc comment
 * for why it is a no-op against this feature's always-opaque canvas today,
 * kept for the moment an eraser or similar leaves a pixel unpainted), not a
 * visible live edit. */
function getDilatedPixelsFor(entry: Entry, iterations: number): Uint8ClampedArray {
  const pixels = Uint8ClampedArray.from(readPixels(entry));
  dilateTexture(pixels, PAINT_CANVAS_SIZE, PAINT_CANVAS_SIZE, iterations);
  return pixels;
}

export interface FxMaterialPaintSession {
  mode: PaintMode;
  brush: BrushSettings;
  dirty: boolean;
  seeded: boolean;
  canUndo: boolean;
  canRedo: boolean;
  texture: CanvasTexture | null;
  canvas: HTMLCanvasElement | null;
  setMode(mode: PaintMode): void;
  setBrush(changes: Partial<BrushSettings>): void;
  seedFrom(pixels: Uint8ClampedArray, width: number, height: number): void;
  seedSolid(colourHex: string): void;
  beginStroke(u: number, v: number): void;
  strokeTo(u: number, v: number): void;
  endStroke(): void;
  undo(): void;
  redo(): void;
  clear(colourHex: string): void;
  markSaved(): void;
  getDilatedPixels(iterations: number): Uint8ClampedArray;
}

/**
 * Acquires (and keeps alive for as long as this component stays mounted) a
 * paint session for `(projectId, materialId)`, shared with any other
 * currently-mounted consumer for the same pair - the cross-panel link
 * `FxMaterialPreviewPanel` (canvas/pointer events) and
 * `FxMaterialPropertiesPanel` (brush controls/Undo/Redo/Save) both need.
 */
export function useFxMaterialPaintSession(projectId: string, materialId: string): FxMaterialPaintSession {
  const [, forceRender] = useReducer((c: number) => c + 1, 0);

  useEffect(() => {
    const entry = acquire(projectId, materialId);
    entry.listeners.add(forceRender);
    forceRender();
    return () => {
      entry.listeners.delete(forceRender);
      release(projectId, materialId);
    };
  }, [projectId, materialId]);

  const entry = getEntry(projectId, materialId);
  // Bound to THIS render's own (projectId, materialId) - deliberately NOT a
  // mutable ref that tracks whatever is currently selected. A caller can
  // hold onto the returned object across an `await` (see `enterPaintMode`
  // in editor-data-fx.tsx, which awaits `loadImagePixels` before calling
  // `seedFrom`) - if the selected material changes in the meantime, this
  // must keep acting on the material it was actually created for, not
  // silently redirect onto whatever is selected by the time the promise
  // resolves. A once-real bug: with a ref-based "always latest" binding,
  // switching materials while a paint-mode seed fetch was still in flight
  // painted the previous material's fetched image onto the newly-selected
  // material's canvas the moment that fetch resolved. `getEntry` returning
  // `undefined` for a torn-down entry (the user navigated away and it
  // wasn't dirty) makes a stale call a safe no-op rather than acting on the
  // wrong material.
  const withEntry = (fn: (entry: Entry) => void) => {
    const current = getEntry(projectId, materialId);
    if (current) fn(current);
  };

  return {
    mode: entry?.mode ?? 'view',
    brush: entry?.brush ?? DEFAULT_BRUSH,
    dirty: entry?.dirty ?? false,
    seeded: entry?.seeded ?? false,
    canUndo: (entry?.undoStack.length ?? 0) > 0,
    canRedo: (entry?.redoStack.length ?? 0) > 0,
    texture: entry?.texture ?? null,
    canvas: entry?.canvas ?? null,
    setMode: (mode) => withEntry((e) => setMode(e, mode)),
    setBrush: (changes) => withEntry((e) => setBrush(e, changes)),
    seedFrom: (pixels, width, height) => withEntry((e) => seedFrom(e, pixels, width, height)),
    seedSolid: (colourHex) => withEntry((e) => seedSolid(e, colourHex)),
    beginStroke: (u, v) => withEntry((e) => beginStroke(e, u, v)),
    strokeTo: (u, v) => withEntry((e) => strokeTo(e, u, v)),
    endStroke: () => withEntry((e) => endStroke(e)),
    undo: () => withEntry((e) => undo(e)),
    redo: () => withEntry((e) => redo(e)),
    clear: (colourHex) => withEntry((e) => clearTo(e, colourHex)),
    markSaved: () => withEntry((e) => markSaved(e)),
    getDilatedPixels: (iterations) => (entry ? getDilatedPixelsFor(entry, iterations) : new Uint8ClampedArray(0)),
  };
}
